import { useQuery } from 'react-query';
import { useToastProvider } from '@qualio/ui-components';
import { ContentTemplate } from '../types';
import contentTemplateApi from '../api/contentTemplate.api';
import { DefaultErrorMessage } from '../displayStrings';
import { isNotFoundError } from '../utils/errorUtils';

type UseContentTemplateDetailsType = {
  data: ContentTemplate | undefined;
  isLoading: boolean;
  isError: boolean;
};

export const useContentTemplateDetails = (
  contentTemplateId: number,
  onSuccess?: (data: ContentTemplate | undefined) => void,
  enabled = true,
): UseContentTemplateDetailsType => {
  const { showToast } = useToastProvider();

  const { data, isLoading, isError } = useQuery(
    ['contentTemplateDetails', contentTemplateId],
    () => contentTemplateApi.getContentTemplateDetails(contentTemplateId),
    {
      enabled: enabled,
      // A missing content template is a terminal outcome: don't retry it (react-query defaults to 3),
      // and let the view render the not-found page instead of firing a generic error toast.
      retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 3,
      onError: (error) => {
        if (isNotFoundError(error)) {
          return;
        }
        showToast({
          description: DefaultErrorMessage,
          status: 'error',
        });
      },
      onSuccess: (data: ContentTemplate) => {
        if (onSuccess) {
          onSuccess(data);
        }
      },
    },
  );

  return { data, isLoading, isError };
};
